import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { ICustomer, ILocation } from 'src/app/features/customers/models/customers.models';
import { IQuotationFilter, IQuotationLocationFilter } from 'src/app/features/quotations/models/quotations.models';
import { encodeFilterObject, refillQuotationFilter } from 'src/app/shared/utils/helpers';

import { RecapHelpersService } from '../recap-helpers.service';
import {
  FilteredOrdersKpi,
  OrdersDisplayKpi,
  RecentOrdersGroup,
} from '../recap-location.types';

/** Orders tab: KPI, chart, and grouped recent orders for one or all locations. */
@Component({
  selector: 'app-location-orders-tab',
  templateUrl: './location-orders-tab.component.html',
  styleUrls: ['./location-orders-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationOrdersTabComponent implements OnChanges {
  @Input() customer: ICustomer | null | undefined = null;

  ordersLocationId: number | 'all' = 'all';
  ordersLoading = false;
  filteredKpi: FilteredOrdersKpi | null = null;
  recentOrdersGrouped: RecentOrdersGroup[] = [];
  chartData: any = null;
  chartOptions: any = null;

  private aggregatedKpi: OrdersDisplayKpi | null = null;
  private aggregatedRecentOrders: any[] = [];

  constructor(
    public readonly recapHelpers: RecapHelpersService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customer']) {
      this.initOrdersDisplay();
    }
  }

  onOrdersLocationChange(value: number | 'all'): void {
    this.ordersLocationId = value;
    this.applyCurrentFilter();
    this.cdr.markForCheck();
  }

  getOrdersLocationName(id: number | 'all'): string {
    if (id === 'all') return this.translateService.instant('companies.orders_tab.all_locations');
    const loc = (this.customer?.locations || []).find((l) => l.id === id);
    return loc?.name || '';
  }

  onLocationMenuOpened(): void {
    setTimeout(() => {
      const active = document.querySelector(
        '.ord-view-menu .ord-view-menu-list .mat-menu-item.active',
      ) as HTMLElement | null;
      active?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }

  toggleRecentOrdersGroup(index: number): void {
    if (this.recentOrdersGrouped[index]) {
      this.recentOrdersGrouped[index].collapsed = !this.recentOrdersGrouped[index].collapsed;
      this.cdr.markForCheck();
    }
  }

  getOrderShippingApartment(order: any): string {
    const locationId = order?.location_id ?? order?.location?.id;
    const locations: ILocation[] = this.customer?.locations || [];
    const location = locations.find((loc) => loc.id === locationId)
      || (this.ordersLocationId !== 'all'
        ? locations.find((loc) => loc.id === this.ordersLocationId)
        : undefined);

    return location?.apartment
      || order?.location?.apartment
      || order?.apartment
      || order?.shipping_address
      || '';
  }


  noOrdersChartData(): boolean {
    const datasets = this.chartData?.datasets || [];
    if (!this.chartData || datasets.length === 0) return true;
    return datasets.every((dataset: any) =>
      !dataset.data?.length || dataset.data.every((value: number) => Number(value) === 0),
    );
  }

  get ordersTrendClass(): string {
    const t = this.filteredKpi?.trend ?? 0;
    if (t > 0) return 'ord-trend-positive';
    if (t < 0) return 'ord-trend-negative';
    return 'ord-trend-neutral';
  }

  get ordersTrendText(): string {
    const t = this.filteredKpi?.trend;
    if (t == null) return '--';
    return (t > 0 ? '+' : '') + t.toFixed(1) + '%';
  }

  openOrderInNewTab(id: number): void {
    window.open('/quotations/' + id, '_blank');
  }

  /**
   * Location ids the CTA should scope the orders list to: the selected location,
   * or every location of the company when the selector is on "all".
   */
  private scopedLocations(): IQuotationLocationFilter[] {
    const locations = this.customer?.locations || [];
    const inScope =
      this.ordersLocationId === 'all'
        ? locations
        : locations.filter((location) => location.id === this.ordersLocationId);
    return inScope
      .filter((location): location is ILocation & { id: number } => location.id != null)
      .map((location) => ({ id: location.id, name: location.name }));
  }

  viewAllOrders(): void {
    const locations = this.scopedLocations();
    // The orders list ingests scope through its own `filter` query param
    // (list-quotations.component.ts:326-341), not a bespoke param. Going through
    // refillQuotationFilter emits exactly the shape the list writes back out, so
    // the two cannot drift.
    const base = refillQuotationFilter();
    const scoped: IQuotationFilter = {
      ...base,
      organizeFilter: { ...base.organizeFilter, locations },
    };
    const filter = locations.length ? encodeFilterObject(scoped) : null;
    const queryParams: { [key: string]: unknown } = { page: 1, count: DEFAULT_PAGE_SIZE };
    if (filter) queryParams['filter'] = filter;
    const url = this.router.serializeUrl(this.router.createUrlTree(['/quotations'], { queryParams }));
    window.open(url, '_blank');
  }


  private initOrdersDisplay(): void {
    if (!this.customer) {
      this.aggregatedKpi = null;
      this.aggregatedRecentOrders = [];
      this.setDisplayState(null, []);
      return;
    }
    const locations = this.customer.locations || [];
    this.aggregatedKpi = this.recapHelpers.aggregateLocationsKpi(locations);
    this.aggregatedRecentOrders = this.recapHelpers.mergeLocationsRecentOrders(locations);
    this.applyCurrentFilter();
  }

  private applyCurrentFilter(): void {
    if (!this.customer) return;
    if (this.ordersLocationId === 'all') {
      this.setDisplayState(this.aggregatedKpi, this.aggregatedRecentOrders);
      return;
    }
    const loc = (this.customer.locations || []).find(l => l.id === this.ordersLocationId);
    const kpi = loc?.kpi;
    const monthly = this.recapHelpers.normalizeMonthlyEntries(kpi?.total_orders_per_month);
    this.setDisplayState(
      {
        total_sales: Number(kpi?.total_sales) || 0,
        total_orders: Number(kpi?.total_orders) || 0,
        total_orders_per_month: monthly,
        peak_ordering_period: this.recapHelpers.normalizePeak(kpi?.peak_ordering_period),
        peak_ordering_period_prev_year: this.recapHelpers.normalizePeak(kpi?.peak_ordering_period_prev_year),
        peak_ordering_period_delta_pct: kpi?.peak_ordering_period_delta_pct != null
          ? Number(kpi.peak_ordering_period_delta_pct)
          : null,
        avg_items_per_order: kpi?.avg_items_per_order ?? null,
        avg_items_per_order_prev_year: kpi?.avg_items_per_order_prev_year ?? null,
        avg_items_per_order_delta_pct: kpi?.avg_items_per_order_delta_pct != null
          ? Number(kpi.avg_items_per_order_delta_pct)
          : null,
      },
      loc?.recent_orders || [],
    );
  }

  private setDisplayState(kpi: OrdersDisplayKpi | null, recentOrders: any[]): void {
    const monthly = kpi?.total_orders_per_month || [];
    this.filteredKpi = {
      total_orders: kpi?.total_orders ?? 0,
      total_sales: kpi?.total_sales ?? 0,
      peak: this.recapHelpers.formatPeakPeriod(kpi?.peak_ordering_period ?? null),
      peakPrev: this.recapHelpers.formatPeakPeriod(kpi?.peak_ordering_period_prev_year ?? null),
      peakDeltaPct: kpi?.peak_ordering_period_delta_pct ?? null,
      avgItems: kpi?.avg_items_per_order ?? null,
      avgItemsPrev: kpi?.avg_items_per_order_prev_year ?? null,
      avgItemsDeltaPct: kpi?.avg_items_per_order_delta_pct ?? null,
      trend: this.recapHelpers.computeYearOverYearTrend(monthly),
    };
    this.chartData = this.recapHelpers.buildChartData(monthly);
    this.chartOptions = this.recapHelpers.buildChartOptions();
    this.recentOrdersGrouped = this.recapHelpers.groupRecentOrders(recentOrders);
    this.ordersLoading = false;
    this.cdr.markForCheck();
  }
}
